import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getGetChronologyEventsQueryKey,
  useGenerateChronologyEvents,
} from "~/api/chronology/chronology";
import { useListDocuments } from "~/api/default/default";
import { DocumentStatus } from "~/api/model";
import { useChatDocumentParams } from "~/helpers";

const AUDIO_EXTENSIONS = [".mp3", ".mp4", ".wav", ".m4a"];

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  DocumentStatus.COMPLETE,
  DocumentStatus.ERROR,
  DocumentStatus.PASSWORD_PROTECTED,
]);

/**
 * Determines whether the case qualifies for chronology generation (all
 * reprocessable documents are fully processed but no events exist yet) and
 * provides a `generate` function that reads existing structured data to
 * populate chronology events without reprocessing documents.
 */
export const useGenerateChronology = () => {
  const { organizationId, caseId } = useChatDocumentParams();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);

  const { mutateAsync } = useGenerateChronologyEvents();

  // Reset generation state when the case changes. React Router can reuse
  // the same component tree for different case params.
  useEffect(() => {
    setIsGenerating(false);
  }, [organizationId, caseId]);

  const { data: documentsData } = useListDocuments(organizationId, caseId);

  const documents = useMemo(
    () => documentsData?.documents ?? [],
    [documentsData?.documents]
  );

  const reprocessable = useMemo(
    () =>
      documents.filter(
        (doc) =>
          !AUDIO_EXTENSIONS.some((ext) =>
            doc.filename.toLowerCase().endsWith(ext)
          )
      ),
    [documents]
  );

  const allReprocessableTerminal =
    reprocessable.length > 0 &&
    reprocessable.every((doc) => TERMINAL_STATUSES.has(doc.status));

  const generate = useCallback(async () => {
    if (reprocessable.length === 0) return;
    setIsGenerating(true);
    try {
      const result = await mutateAsync({
        organizationId,
        projectId: caseId,
      });

      await queryClient.invalidateQueries({
        queryKey: getGetChronologyEventsQueryKey(organizationId, caseId),
      });

      if (result.sectionsFailed > 0) {
        return { partialFailureCount: result.sectionsFailed };
      }

      return undefined;
    } finally {
      setIsGenerating(false);
    }
  }, [reprocessable, organizationId, caseId, queryClient, mutateAsync]);

  return {
    canGenerate:
      allReprocessableTerminal &&
      reprocessable.some((doc) => doc.status === DocumentStatus.COMPLETE),
    isGenerating,
    generate,
  };
};
